import { asyncFrame } from '@1inch-community/core/async';
import { LongTimeAsyncCache } from '@1inch-community/core/cache';
import { getMobileMatchMedia } from '@1inch-community/core/lit-utils';
import { getContainer } from '@1inch-community/ui-components/overlay';
import { html, render } from 'lit';
import { distinctUntilChanged, map, startWith, Subject } from 'rxjs';
import { NotificationsDesktopContainerElement, NotificationsMobileContainerElement, } from './elements';
import { getNotificationId } from './notifications-id';
export class NotificationsManager {
    constructor() {
        this.cache = new LongTimeAsyncCache('notifications', 3);
        this.notifications = new Map();
        this.mobileMatchMedia = getMobileMatchMedia();
        this.activeContainer = null;
        this.update$ = new Subject();
        this.notificationsCount$ = this.update$.pipe(startWith(null), map(() => this.notifications.size), distinctUntilChanged());
    }
    async init() {
        const records = await this.cache.getAll();
        for (const record of records) {
            const notification = JSON.parse(record);
            const templateElement = document.createElement('template');
            templateElement.innerHTML = notification.template.trim();
            const element = templateElement.content.firstChild;
            this.notifications.set(notification.id, {
                ...notification,
                element,
            });
        }
        this.update$.next();
    }
    async openAllNotifications() {
        const container = this.getNotificationsContainer();
        if (!getContainer().contains(container)) {
            await this.renderContainer();
        }
        container.openAll();
    }
    async closeNotifications() {
        const container = this.getNotificationsContainer();
        await container.preClose();
        container.remove();
        this.activeContainer = null;
        this.update$.next();
    }
    async removeNotifications() {
        this.notifications.clear();
        await this.cache.clear();
        await this.closeNotifications();
        this.update$.next();
    }
    async removeNotification(id) {
        if (!this.notifications.has(id))
            return;
        this.notifications.delete(id);
        await this.cache.delete(id);
        if (this.notifications.size === 0) {
            await this.closeNotifications();
            return;
        }
        const container = this.getNotificationsContainer();
        if (getContainer().contains(container)) {
            container.setAllNotifications(this.getSortedNotifications());
        }
        else {
            await this.renderContainer();
        }
        this.update$.next();
    }
    async error(template) {
        return await this.show('Error', template, { errorStyle: true });
    }
    async warning(template) {
        return await this.show('Warning', template, { warningStyle: true });
    }
    async show(title, template, config) {
        return await this.render(template, { ...config, title });
    }
    async render(template, config) {
        const id = getNotificationId();
        const templateResult = typeof template === 'string' ? html `${template}` : template;
        const element = document.createElement('div');
        render(templateResult, element);
        await asyncFrame();
        const record = {
            id,
            element: element,
            config: config ?? {},
            timestamp: Date.now(),
        };
        this.notifications.set(id, record);
        if (!(config?.notCache ?? false)) {
            await this.cacheNotification(id, record);
        }
        const container = this.getNotificationsContainer();
        if (getContainer().contains(container)) {
            container.setAllNotifications(this.getSortedNotifications());
        }
        else {
            await this.renderContainer();
        }
        this.update$.next();
        return id;
    }
    async renderContainer() {
        const container = this.getNotificationsContainer();
        container.setAllNotifications(this.getSortedNotifications());
        await container.preRender();
        getContainer().appendChild(container);
        await container.postRender();
        this.update$.next();
    }
    getNotificationsContainer() {
        if (this.activeContainer !== null) {
            return this.activeContainer;
        }
        let container;
        if (this.mobileMatchMedia.matches) {
            container = document.createElement(NotificationsMobileContainerElement.tagName);
        }
        else {
            container = document.createElement(NotificationsDesktopContainerElement.tagName);
        }
        container.setController(this);
        this.activeContainer = container;
        return container;
    }
    async cacheNotification(id, record) {
        try {
            const serializer = new XMLSerializer();
            const templateString = serializer.serializeToString(record.element);
            const recordString = JSON.stringify({
                ...record,
                template: templateString,
                element: undefined,
            });
            await this.cache.set(id, recordString);
        }
        catch (error) {
            console.error(error);
        }
    }
    getSortedNotifications() {
        return [...this.notifications.entries()].sort((r1, r2) => {
            const n1 = r1[1];
            const n2 = r2[1];
            return n2.timestamp - n1.timestamp;
        });
    }
}
//# sourceMappingURL=notifications-controller.js.map